import { auth } from '../lib/config/firebaseInit';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { goToLogin } from './authRedirect';

// User type configurations
const USER_TYPES = {
  elite: {
    claim: 'elite',
    loginPath: '/login',
    unauthorizedPath: '/unauthorized',
    fallbackPath: '/login'
  },
  operations: {
    claim: 'operations',
    loginPath: '/login',
    unauthorizedPath: '/unauthorized',
    fallbackPath: '/login'
  },
  sales: {
    claim: 'sales',
    loginPath: '/login',
    unauthorizedPath: '/unauthorized',
    fallbackPath: '/login'
  },
  merchant: {
    claim: 'merchant',
    loginPath: '/login',
    unauthorizedPath: '/unauthorized',
    fallbackPath: '/login'
  },
  customer: {
    claim: 'customer',
    loginPath: '/login',
    unauthorizedPath: '/unauthorized',
    fallbackPath: '/login'
  }
};

export const useAuthCheck = (requiredType = 'elite', options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState({
    isAuthorized: false,
    isLoading: true,
    user: null,
    userType: null,
    error: null
  });

  useEffect(() => {
    let unsubscribe;
    let timeoutId;

    const checkAuth = async () => {
      try {
        // Wait for auth state to be ready with timeout
        const user = await new Promise((resolve, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Auth timeout')), 10000);
          
          unsubscribe = auth.onAuthStateChanged((user) => {
            clearTimeout(timeoutId);
            resolve(user);
          });
        });
        
        if (!user) {
          throw new Error('No user found');
        }

        // Get user claims
        const idTokenResult = await user.getIdTokenResult(true);
        const claims = idTokenResult.claims;
        
        // Check if user has the required claim
        const userType = USER_TYPES[requiredType];
        if (!userType) {
          throw new Error(`Invalid user type: ${requiredType}`);
        }

        const hasClaim = claims[userType.claim] === true;

        if (!hasClaim) {
          // Check if user has any other valid claims
          const availableClaims = Object.keys(USER_TYPES).filter(type => claims[type] === true);
          
          if (availableClaims.length > 0) {
            // User has different permissions, redirect to appropriate login
            const redirectType = availableClaims[0];
            const redirectPath = USER_TYPES[redirectType].loginPath;
            navigate(redirectPath, { 
              replace: true,
              state: { 
                message: `You need ${requiredType} access. Please login with appropriate credentials.`,
                requiredType 
              }
            });
            return;
          } else {
            // No valid claims, redirect to unauthorized
            navigate(userType.unauthorizedPath, { 
              replace: true,
              state: { 
                message: 'Access denied. Insufficient permissions.',
                requiredType 
              }
            });
          return;
          }
        }

        // User is authorized
        setAuthState({
          isAuthorized: true,
          isLoading: false,
          user,
          userType: requiredType,
          error: null
        });

      } catch (error) {
        console.error('Auth check error:', error);
        
        const userType = USER_TYPES[requiredType];
        const errorMessage = error.message === 'Auth timeout' 
          ? 'Authentication timeout. Please try again.'
          : 'Authentication failed. Please login again.';

        setAuthState({
          isAuthorized: false,
          isLoading: false,
          user: null,
          userType: null,
          error: errorMessage
        });

        // Navigate to appropriate login page, remembering where we were
        goToLogin(navigate, location);
      }
    };

    checkAuth();

    // Cleanup function
    return () => {
      if (unsubscribe) unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, requiredType]);

  // Helper functions for easy integration
  const logout = async () => {
    try {
      await auth.signOut();
      const userType = USER_TYPES[requiredType];
      navigate(userType.loginPath, { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const refreshAuth = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      const user = auth.currentUser;
      if (user) {
        await user.getIdToken(true);
        // Re-check authorization
        const idTokenResult = await user.getIdTokenResult();
        const hasClaim = idTokenResult.claims[USER_TYPES[requiredType].claim] === true;
        
        setAuthState(prev => ({
          ...prev,
          isAuthorized: hasClaim,
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Refresh auth error:', error);
      logout();
    }
  };

  return {
    ...authState,
    logout,
    refreshAuth,
    // Convenience getters
    isElite: authState.userType === 'elite',
    isOperations: authState.userType === 'operations',
    isSales: authState.userType === 'sales',
    isMerchant: authState.userType === 'merchant',
    isCustomer: authState.userType === 'customer'
  };
};

// Convenience hooks for specific user types
export const useEliteAuth = (options) => useAuthCheck('elite', options);
export const useOperationsAuth = (options) => useAuthCheck('operations', options);
export const useSalesAuth = (options) => useAuthCheck('sales', options);
export const useMerchantAuth = (options) => useAuthCheck('merchant', options);
export const useCustomerAuth = (options) => useAuthCheck('customer', options);

// Hook for checking multiple user types
export const useMultiAuthCheck = (allowedTypes = ['elite', 'merchant'], options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState({
    isAuthorized: false,
    isLoading: true,
    user: null,
    userType: null,
    error: null
  });

  useEffect(() => {
    let unsubscribe;
    let timeoutId;

    const checkAuth = async () => {
      try {
        const user = await new Promise((resolve, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Auth timeout')), 10000);
          
          unsubscribe = auth.onAuthStateChanged((user) => {
            clearTimeout(timeoutId);
            resolve(user);
          });
        });

        if (!user) {
          throw new Error('No user found');
        }

        const idTokenResult = await user.getIdTokenResult();
        const claims = idTokenResult.claims;

        // Check if user has any of the allowed claims
        const userType = allowedTypes.find(type => claims[USER_TYPES[type].claim] === true);
        
        if (!userType) {
          navigate('/unauthorized', { 
            replace: true,
            state: { 
              message: 'Access denied. Insufficient permissions.',
              allowedTypes 
            }
          });
          return;
        }

        setAuthState({
          isAuthorized: true,
          isLoading: false,
          user,
          userType,
          error: null
        });

      } catch (error) {
        console.error('Multi auth check error:', error);
        setAuthState({
          isAuthorized: false,
          isLoading: false,
          user: null,
          userType: null,
          error: error.message
        });
        goToLogin(navigate, location);
      }
    };

    checkAuth();

    return () => {
      if (unsubscribe) unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, allowedTypes.join(',')]);

  const logout = async () => {
    try {
      await auth.signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return {
    ...authState,
    logout
  };
};